import React, { useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { FolderChecked } from 'icons';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  assignUsers,
  PlanUsersReport,
  trainTogetherMarkPlanAsComplete,
  UserPlans,
} from '@cfa/pathway-fe-shared/xp';

import { useInvalidateCertificateQueries } from '@/hooks/useInvalidateCertificateQueries';

import { formatDateAsLocal, formatDateAsUtc } from '@/utils/date';
import { generateProgressText } from '@/utils/generateProgressText';
import { invalidateAllPlanQueries } from '@/utils/queryInvalidation';

import PlanCardIcon from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardIcon';
import { PathwayCard } from '@/components/pathway-card';
import ConfirmationModal from '@/components/popups/ConfirmationModal';
import { DueDatePopUp } from '@/components/popups/DueDatePopUp';
import SuccessToast from '@/components/Toasts/SuccessToast';

import Badge from './components/Badge';
import ManageMenu from './components/ManageMenu';
import ResetProgressModal from './components/ResetProgressModal';
import type { TeamMemberRow } from './teamMemberRow';
import { useTeamMemberCard } from './useTeamMemberCard';

interface TeamMemberCardProps {
  member: TeamMemberRow;
  plan?: UserPlans;
  planReport?: PlanUsersReport;
  reportsRoute?: boolean;
  onUpdateDueDate?: (id: string, name: string) => void;
  onUnassign?: (id: string, name: string) => void;
}

const TeamMemberCard: React.FC<TeamMemberCardProps> = ({
  member,
  plan,
  planReport,
  reportsRoute,
  onUnassign,
  onUpdateDueDate,
}) => {
  const invalidateCertificates = useInvalidateCertificateQueries();
  const { t } = useTranslation();

  const {
    title,
    displayName,
    href,
    isComplete,
    isFailed,
    isReports,
    isReportsPlansView,
    selectedDueDate,
    stepCount,
    progressBarText,
    progressPercent,
    showProgress,
    planId,
    planName,
    isCompliance,
    complianceDueDateOffset,
    userAllowedToUpdateTMDueDateOnPlan,
    userCanMarkAsComplete,
    hasPreviouslyCompletedPlanAssignment,
    previouslyCompletedPlanAssignmentDate,
  } = useTeamMemberCard({ member, plan, planReport, reportsRoute });

  const [showMarkCompletedPopup, setShowMarkCompletedPopup] = useState(false);
  const [isDueDatePopUpOpen, setIsDueDatePopUpOpen] = useState(false);

  const handleUnassign = () => {
    onUnassign?.(member.id, title);
  };

  const handleUpdateDueDate = () => {
    onUpdateDueDate?.(member.id, title);
  };

  const handleMarkComplete = () => {
    setShowMarkCompletedPopup(true);
  };

  const onMarkCompleteCancel = () => {
    setShowMarkCompletedPopup(false);
  };

  const onMarkCompleteConfirm = () => {
    if (!planId) {
      datadogRum.addError(new Error('[TeamMemberCard] Plan ID is required'));
      return;
    }

    setShowMarkCompletedPopup(false);
    trainTogetherMarkPlanAsComplete({
      path: { planId },
      body: { userIds: [member.id] },
    })
      .then(async () => {
        await Promise.all([
          invalidateCertificates({ planId, userId: member.id }),
          invalidateAllPlanQueries(planId),
        ]);
      })
      .catch((err: unknown) => {
        datadogRum.addError(err);
      });
  };

  const handleReassignConfirm = async (dueDate: string | null) => {
    if (!planId) {
      datadogRum.addError(new Error('[TeamMemberCard] Plan ID is required'));
      return;
    }
    setIsDueDatePopUpOpen(false);

    const assignResult = await assignUsers({
      path: { planId },
      body: {
        userIds: [member.id],
        dueDate: dueDate ?? (null as unknown as undefined),
      },
    });

    if (!assignResult.error) {
      toast.custom(toastObj => (
        <SuccessToast id={toastObj.id} severity="success">
          {t('TrainingPlans.toastMessage.assignedTeamMember')}
        </SuccessToast>
      ));
      await Promise.all([
        invalidateCertificates({ planId, userId: member.id }),
        invalidateAllPlanQueries(planId),
      ]);
    }

    if (assignResult.error) {
      datadogRum.addError(
        new Error(
          `Failed to reassign plan (planId=${planId}, userId=${member.id}): ${assignResult.error.message ?? 'Unknown error'}`,
        ),
      );
    }
  };

  const handleReassign = () => {
    if (isCompliance && complianceDueDateOffset) {
      const withOffset = new Date();
      withOffset.setDate(withOffset.getDate() + complianceDueDateOffset);

      handleReassignConfirm(withOffset.toISOString());
    } else {
      setIsDueDatePopUpOpen(true);
    }
  };

  const isCompleteAndReports = isComplete && isReports;

  return (
    <>
      <PathwayCard.Root
        link={href}
        className="animate-[fadeIn_linear_0.3s] overflow-visible"
        data-testid="TeamMemberCard"
      >
        <PathwayCard.Body data-displayname={displayName} className="sm:mx-4">
          <PathwayCard.Title data-testid="PlanName">{title}</PathwayCard.Title>
          {isCompleteAndReports ? (
            <>
              <PathwayCard.Subtitle tone="success" data-testid="PlanTaskCount">
                {generateProgressText({
                  completedStepCount: member.completedStepCount,
                  isCompliance: false,
                  isComplete,
                  isFailed,
                  isFoodSafety: false,
                  timeSpentOnPlan: member.timeSpentOnPlan,
                  stepCount,
                })}
              </PathwayCard.Subtitle>
              {!!member.startDate && (
                <PathwayCard.Subtitle data-testid="PlanTaskCount">
                  {`${t('Reports.filtering.startDate')}: ${formatDateAsUtc(member.startDate)}`}
                </PathwayCard.Subtitle>
              )}
            </>
          ) : (
            <>
              {isReports ? (
                <div className="text-left">
                  <PathwayCard.Subtitle
                    className="whitespace-nowrap"
                    data-testid="ProgressBarText"
                  >
                    {progressBarText}
                  </PathwayCard.Subtitle>
                  {!!member.startDate && (
                    <PathwayCard.Subtitle>
                      {`${t('Reports.filtering.startDate')}: ${formatDateAsUtc(member.startDate)}`}
                    </PathwayCard.Subtitle>
                  )}
                </div>
              ) : (
                <PathwayCard.Subtitle
                  tone={isComplete ? 'success' : 'default'}
                  className="whitespace-nowrap"
                  data-testid="ProgressBarText"
                >
                  {progressBarText}
                </PathwayCard.Subtitle>
              )}
            </>
          )}
        </PathwayCard.Body>
        <PathwayCard.Meta>
          <div className="flex shrink-0 flex-wrap items-center justify-start gap-2 sm:gap-4">
            {hasPreviouslyCompletedPlanAssignment && !isComplete && (
              <PlanCardIcon
                icon={<FolderChecked className="size-6" />}
                tooltip={
                  <p className="text-variant-body2 text-grayscale-gray7 font-normal">
                    {t('TrainingPlans.statusOptions.lastCompleted')}{' '}
                    {formatDateAsLocal(previouslyCompletedPlanAssignmentDate)}
                  </p>
                }
              />
            )}
            <Badge
              isComplete={isComplete}
              completionDate={member.completionDate}
              selectedDueDate={selectedDueDate}
              showDate
            />
          </div>
        </PathwayCard.Meta>
        {showProgress && (
          <PathwayCard.Progress
            className="sm:pl-4"
            aria-label="Plan progress"
            value={progressPercent}
          />
        )}
        <PathwayCard.Actions>
          <ManageMenu
            id={member.id}
            name={title}
            isComplete={isComplete}
            isReports={isReports}
            isReportsPlansView={isReportsPlansView}
            completedStepCount={member.completedStepCount}
            handleReassign={handleReassign}
            handleUnassign={handleUnassign}
            handleUpdateDueDate={handleUpdateDueDate}
            handleMarkComplete={handleMarkComplete}
            userAllowedToUpdateTMDueDateOnPlan={
              userAllowedToUpdateTMDueDateOnPlan
            }
            userCanMarkAsComplete={userCanMarkAsComplete}
          />
        </PathwayCard.Actions>
      </PathwayCard.Root>
      <ConfirmationModal
        bodyText={`${t('TrainingPlans.markCompleted1')} <b>${planName}</b> ${t(
          'TrainingPlans.markCompleted2',
        )}`}
        headerText={t('Generic.markCompleted')}
        isOpen={showMarkCompletedPopup}
        onClose={onMarkCompleteCancel}
        primaryButtonHandler={onMarkCompleteConfirm}
        primaryButtonText={t('Button.yes')}
        secondaryButtonHandler={onMarkCompleteCancel}
        secondaryButtonText={t('Button.cancel')}
      />
      <DueDatePopUp
        isOpen={isDueDatePopUpOpen}
        onClose={() => setIsDueDatePopUpOpen(false)}
        onSave={handleReassignConfirm}
      />
      <ResetProgressModal
        teamMemberId={member.id}
        teamMemberName={title}
        planId={planId}
        planName={planName}
      />
    </>
  );
};

export default TeamMemberCard;
